import { useQuery, useMutation } from '@tanstack/react-query';
import { WorkflowSimulatorService } from '../services/workflow-simulator-service';
import httpClient from '../services/http-client';
import {
  SimulateMatchRequest,
  SimulateExecutionRequest,
  FullSimulationRequest,
  WorkflowSimulationResult,
  ExecutionSimulationResult,
  ApproverPreviewResult,
  FullSimulationResult,
  WorkflowRecommendationRequest,
  WorkflowRecommendationResult
} from '../types';

// Create a simulator service instance
const simulatorService = new WorkflowSimulatorService(httpClient);

// Query keys for simulator
export const simulatorQueryKeys = {
  all: ['simulator'] as const,
  approverPreview: (divisionId: string, workflowId: string) =>
    [...simulatorQueryKeys.all, 'approvers', divisionId, workflowId] as const,
  recommendations: (divisionId: string) =>
    [...simulatorQueryKeys.all, 'recommendations', divisionId] as const,
};

/**
 * Hook to preview approvers for a workflow
 */
export function useApproverPreview(
  divisionId: string,
  workflowId: string,
  enabled = true
) {
  return useQuery({
    queryKey: simulatorQueryKeys.approverPreview(divisionId, workflowId),
    queryFn: () => simulatorService.previewApprovers(divisionId, workflowId),
    enabled: enabled && !!divisionId && !!workflowId,
    staleTime: 30 * 1000, // 30 seconds - shorter since this is preview data
  });
}

/**
 * Hook to simulate workflow match
 */
export function useSimulateMatch(divisionId: string) {
  return useMutation({
    mutationFn: (request: SimulateMatchRequest) =>
      simulatorService.simulateMatch(divisionId, request),
  });
}

/**
 * Hook to simulate workflow execution
 */
export function useSimulateExecution(divisionId: string, workflowId: string) {
  return useMutation({
    mutationFn: (request?: SimulateExecutionRequest) =>
      simulatorService.simulateExecution(divisionId, workflowId, request),
  });
}

/**
 * Hook to run full simulation (match + execution)
 */
export function useSimulateFull(divisionId: string) {
  return useMutation({
    mutationFn: (request: FullSimulationRequest) =>
      simulatorService.simulateFull(divisionId, request),
  });
}

/**
 * Hook to get workflow recommendations based on order data
 */
export function useWorkflowRecommendations(divisionId: string) {
  return useMutation({
    mutationFn: (request: WorkflowRecommendationRequest) =>
      simulatorService.getRecommendations(divisionId, request),
  });
}

/**
 * Combined hook for all simulation operations
 */
export function useWorkflowSimulator(divisionId: string) {
  const simulateMatch = useSimulateMatch(divisionId);
  const simulateFull = useSimulateFull(divisionId);
  const recommendations = useWorkflowRecommendations(divisionId);

  return {
    simulateMatch: simulateMatch.mutateAsync,
    isMatchLoading: simulateMatch.isPending,
    matchError: simulateMatch.error,
    matchResult: simulateMatch.data,

    simulateFull: simulateFull.mutateAsync,
    isFullLoading: simulateFull.isPending,
    fullError: simulateFull.error,
    fullResult: simulateFull.data,

    getRecommendations: recommendations.mutateAsync,
    isRecommendationsLoading: recommendations.isPending,
    recommendationsError: recommendations.error,
    recommendationsResult: recommendations.data,

    isLoading: simulateMatch.isPending || simulateFull.isPending || recommendations.isPending,
    reset: () => {
      simulateMatch.reset();
      simulateFull.reset();
      recommendations.reset();
    },
  };
}
